import { JSX } from "react";
import { Control, Controller } from "react-hook-form";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import { startOfDay } from "date-fns";
import { z } from "zod";
import {
  Textarea,
  OFFLINE_REASON_OPTIONS,
  OFFLINE_REASON_VALUES,
  Typography,
  TextField,
} from "@platform/design-system";

const MIN_DESCRIPTION_LENGTH = 25;
const MAX_DESCRIPTION_LENGTH = 1000;
// Whole numbers 0-99; kept as strings in form state so an empty field is
// "not yet entered" rather than silently coerced to 0.
const plansQuotedValueSchema = z
  .string()
  .regex(/^\d{1,2}$/, "Enter a whole number from 0 to 99");

export const DATE_SENT_TO_CARRIERS_HELP =
  "Enter the date carriers initially received this RFP. If this is a requote, enter the date the updated request was sent to carriers.";

export const offlineSubmissionSchema = z.object({
  offlineReason: z.enum(OFFLINE_REASON_VALUES),
  offlineDescription: z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().min(MIN_DESCRIPTION_LENGTH).max(MAX_DESCRIPTION_LENGTH)),
  dateSentToCarriers: z
    .date()
    .refine((value) => startOfDay(value) <= startOfDay(new Date()), {
      message: "Date cannot be in the future",
    }),
  offlinePlansQuoted: z.record(z.string(), plansQuotedValueSchema),
});

export type OfflineSubmissionFormData = z.infer<typeof offlineSubmissionSchema>;

/** A line of coverage the analyst enters a plans-quoted count for. */
export type OfflinePlansQuotedCoverage = {
  type: string;
  label: string;
};

export type OfflineSubmissionFormProps = {
  control: Control<OfflineSubmissionFormData>;
  coverages: OfflinePlansQuotedCoverage[];
};

const OfflineSubmissionForm = ({
  control,
  coverages,
}: OfflineSubmissionFormProps): JSX.Element => (
  <Box display="flex" flexDirection="column" gap={3}>
    <Controller
      control={control}
      name="offlineReason"
      render={({ field, fieldState }) => (
        <FormControl fullWidth>
          <InputLabel id="offlineReason" color="brandPrimary">
            Reason for offline submission
          </InputLabel>
          <Select
            {...field}
            label="Reason for offline submission"
            id="offlineReason"
            color="brandPrimary"
            value={field.value || ""}
            error={Boolean(fieldState.error)}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {OFFLINE_REASON_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
    <Controller
      control={control}
      name="offlineDescription"
      render={({ field }) => (
        <Textarea
          {...field}
          label="Description"
          fullWidth
          placeholder="To better understand the various complexities of each RFP, please outline specific details for why this RFP was taken offline."
          counterText="%value characters (minimum %minLength characters)"
          minLength={MIN_DESCRIPTION_LENGTH}
          maxLength={MAX_DESCRIPTION_LENGTH}
        />
      )}
    />
    <Controller
      control={control}
      name="dateSentToCarriers"
      render={({ field, fieldState }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <DatePicker
            {...field}
            label="Date Sent to Carriers"
            format="MM/dd/yyyy"
            disableFuture
            slotProps={{
              textField: {
                fullWidth: true,
                color: "brandPrimary",
                error: Boolean(fieldState.error),
                helperText: fieldState.error?.message,
              },
            }}
          />
          <Tooltip title={DATE_SENT_TO_CARRIERS_HELP}>
            <IconButton
              size="small"
              aria-label="Date Sent to Carriers help"
              // Nothing to activate — the button exists so the tooltip is
              // reachable by keyboard and announced by screen readers.
              disableRipple
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    />
    {coverages.length > 0 && (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2" color="textSecondary">
          Total plans quoted
        </Typography>
        {coverages.map((coverage) => (
          <Controller
            key={coverage.type}
            control={control}
            name={`offlinePlansQuoted.${coverage.type}`}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                label={coverage.label}
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    maxLength: 2,
                    "aria-label": `Total plans quoted for ${coverage.label}`,
                  },
                }}
              />
            )}
          />
        ))}
      </Box>
    )}
  </Box>
);

export default OfflineSubmissionForm;
